/**
 * Admin V3 — Drivers — Add driver modal (P1-B3-3b)
 *
 * Migration of the legacy V1 "Nouveau chauffeur" dialog (AdminDashboard.js:445).
 * Creates a driver via the EXISTING endpoint POST /driver/register with the same
 * fields V1 used (name, email, password, phone, vehicle_model, license_plate).
 *
 * ⚠️ RBAC LIMITATION (AV3-31, owner-acknowledged — Option A):
 *   POST /driver/register is a PUBLIC self-registration route (@public_route);
 *   the backend does NOT RBAC-gate driver creation. The "Add driver" affordance
 *   is therefore gated in the FRONTEND ONLY (cosmetic) — it is NOT enforced
 *   server-side. A dedicated RBAC-protected POST /admin/drivers is a documented
 *   backend-hardening follow-up (see AV3-31); NOT implemented here.
 *
 * Under BETA_MODE (ON in prod) a new driver is created as a CANDIDATE /
 * pending-approval (is_approved:false, lifecycle_state:"candidate"); the success
 * message states this. The returned driver token is ignored by the admin flow.
 * No backend change.
 */
import React, { useState } from 'react';
import { Loader2, X, UserPlus, Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100';

// Module-scope field (defining it inside the component would remount inputs on
// every keystroke and lose focus).
const Field = ({ label, k, value, onChange, type = 'text', required }) => (
  <div>
    <label className="text-[12px] font-semibold text-slate-600">
      {label}{required && <span className="text-rose-500"> *</span>}
    </label>
    <input type={type} value={value} onChange={onChange} className={inputCls} data-testid={`adminv3-driver-add-${k}`} />
  </div>
);

const AddDriverModal = ({ open, onClose, onDone, locale = DEFAULT_LOCALE }) => {
  // Reuse the existing public registration endpoint (no backend change).
  const m = useAdminMutation('/driver/register', 'post');
  const { showSuccess, showError } = useToast();

  const [f, setF] = useState({
    name: '', email: '', password: '', phone: '', vehicle_model: '', license_plate: '',
  });
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  if (!open) return null;

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  // Backend-required: name, email, password, vehicle_model, license_plate (phone optional).
  const valid = f.name.trim() && emailOk && f.password && f.vehicle_model.trim() && f.license_plate.trim();

  const submit = () => {
    m.mutate(
      {
        name: f.name.trim(),
        email: f.email.trim(),
        password: f.password,
        phone: f.phone.trim() || null,
        vehicle_model: f.vehicle_model.trim(),
        license_plate: f.license_plate.trim(),
      },
      {
        onSuccess: () => {
          // BETA_MODE → created as candidate / pending approval.
          showSuccess(t('drivers.add.ok.pending', locale));
          if (onDone) onDone();
          onClose();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('drivers.add.error', locale)),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-driver-add-modal">
      <div className="w-[560px] max-w-[94vw] max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-[17px] font-semibold text-slate-900">
            <UserPlus className="h-4 w-4 text-emerald-500" />{t('drivers.add.title', locale)}
          </h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-driver-add-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* New drivers require approval (BETA). Set expectations up front. */}
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[12px] text-amber-800" data-testid="adminv3-driver-add-pending-note">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          {t('drivers.add.pendingNote', locale)}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label={t('drivers.add.field.name', locale)}         k="name"          value={f.name}          onChange={set('name')}          required />
          <Field label={t('drivers.add.field.email', locale)}        k="email"         value={f.email}         onChange={set('email')}         type="email" required />
          <Field label={t('drivers.add.field.password', locale)}     k="password"      value={f.password}      onChange={set('password')}      type="password" required />
          <Field label={t('drivers.add.field.phone', locale)}        k="phone"         value={f.phone}         onChange={set('phone')} />
          <Field label={t('drivers.add.field.vehicleModel', locale)} k="vehicle_model" value={f.vehicle_model} onChange={set('vehicle_model')} required />
          <Field label={t('drivers.add.field.licensePlate', locale)} k="license_plate" value={f.license_plate} onChange={set('license_plate')} required />
        </div>

        {!valid && (
          <p className="mt-3 text-[12px] text-rose-600" data-testid="adminv3-driver-add-missing">
            {t('drivers.add.required', locale)}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-driver-add-dismiss">
            {t('drivers.add.cancel', locale)}
          </Button>
          <Button
            variant="default" size="sm" onClick={submit} disabled={!valid || m.loading}
            data-testid="adminv3-driver-add-confirm"
          >
            {m.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {t('drivers.add.confirm', locale)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddDriverModal;
